/**
 * Interview Feedback Page
 * Displays detailed feedback blocks for completed interview assessment
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAssessmentFeedback,
  resetAssessment,
} from "@/store/slices/interviewAssessmentSlice";
import {
  selectFeedbackBlocks,
  selectIsFetchingFeedback,
  selectFetchFeedbackError,
  selectFeedback,
} from "@/store/selectors/interviewAssessmentSelectors";
import { selectDashboardSessions, selectDashboardLoading } from "@/store/selectors/dashboardSelectors";
import { fetchDashboardSessions } from "@/store/slices/dashboardSlice";
import { FeedbackTabsLayout } from "@/components/feedback/FeedbackTabsLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  RotateCcw,
  Download,
  Share2,
  Trophy,
  HelpCircle,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  generateFeedbackPDF,
  shareFeedback,
  isShareSupported,
} from "@/utils/feedbackExport";
import { GaugeBlock } from "@/types/interview-feedback.types";
import { logger } from "@/utils/logger";
import { selectTotalModulesCountRounded } from "@/store/selectors/meetingModulesSelectors";
import { useProduct } from "@/hooks/useProduct";
import { checkQuizStatusThunk, setPolling } from "@/store/slices/quizSlice";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";

export default function InterviewFeedback() {
  const config = useProduct();
  const navigate = useNavigate();
  const { assessmentId, meetingId } = useParams<{
    assessmentId: string;
    meetingId?: string;
  }>();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  // Determine back path based on origin
  const backPath = meetingId ? "/dashboard" : "/dashboard";

  // Redux state
  const feedbackBlocks = useAppSelector(selectFeedbackBlocks);
  const isFetchingFeedback = useAppSelector(selectIsFetchingFeedback);
  const fetchFeedbackError = useAppSelector(selectFetchFeedbackError);
  const feedback = useAppSelector(selectFeedback);
  const dashboardSessions = useAppSelector(selectDashboardSessions);
  const dashboardLoading = useAppSelector(selectDashboardLoading);
  const totalModulesCount = useAppSelector(selectTotalModulesCountRounded);
  const { isPolling } = useAppSelector((state) => state.quiz);

  // Resolve actual sessionId (meetingId from URL is module.id if coming from simulation, or session.id if coming from dashboard)
  const actualSessionId = useMemo(() => {
    if (!meetingId) return undefined;
    return MeetingSessionManager.get(meetingId) || meetingId;
  }, [meetingId]);

  // Quiz Polling Logic
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 20 attempts * 3 seconds = 60 seconds max

    if (isPolling && actualSessionId) {
      intervalId = setInterval(async () => {
        attempts += 1;
        
        if (attempts >= MAX_ATTEMPTS) {
          dispatch(setPolling(false));
          toast({
            title: "Timeout",
            description: "Quiz generation is taking too long. Please try again later.",
            variant: "destructive",
          });
          clearInterval(intervalId);
          return;
        }

        const resultAction = await dispatch(checkQuizStatusThunk(actualSessionId));
        if (checkQuizStatusThunk.fulfilled.match(resultAction)) {
          const newStatus = resultAction.payload.status;
          if (newStatus === "completed") {
            dispatch(setPolling(false));
            navigate(`/meeting/${actualSessionId}/quiz/generated`);
          } else if (newStatus === "error") {
            dispatch(setPolling(false));
            toast({
              title: "Error",
              description: "Failed to generate quiz. Please try again.",
              variant: "destructive",
            });
          }
        }
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, actualSessionId, dispatch, navigate, toast]);

  // Get meeting info from dashboard session if available
  const meetingSession = useMemo(() => {
    if (actualSessionId) {
      return dashboardSessions.find((session) => session.id === actualSessionId);
    }
    return null;
  }, [actualSessionId, dashboardSessions]);

  // Fetch dashboard sessions if we don't have the current session
  useEffect(() => {
    if (meetingId && !meetingSession && !dashboardLoading) {
      // Force refresh to ensure we get the latest session data including realtime_transcript
      dispatch(fetchDashboardSessions(true));
    }
  }, [meetingId, meetingSession, dashboardLoading, dispatch]);

  // Use meeting info from session or fallback to feedback data
  const meetingName =
    meetingSession?.meeting_name ||
    feedback?.meeting_name ||
    "Your Conversation Performance";
  const meetingCategory =
    meetingSession?.meeting_category || feedback?.meeting_category;

  // Fetch feedback on mount
  useEffect(() => {
    if (assessmentId) {
      logger.log("[InterviewFeedback] Fetching feedback for:", assessmentId);
      dispatch(fetchAssessmentFeedback(assessmentId));
    } else {
      logger.warn("[InterviewFeedback] No assessment ID provided");
      toast({
        title: config.i18n.interviewFeedback.invalidAssessment,
        description: config.i18n.interviewFeedback.invalidAssessment,
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [assessmentId, dispatch, navigate, toast, config.i18n.interviewFeedback]);

  // Debug: Log feedback dates when feedback changes
  useEffect(() => {
    if (feedback) {
      logger.log("[InterviewFeedback] Feedback dates:", {
        created_at: feedback.created_at,
        completed_at: feedback.completed_at,
        created_at_type: typeof feedback.created_at,
        completed_at_type: typeof feedback.completed_at,
      });
    }
  }, [feedback]);

  // Handle fetch error
  useEffect(() => {
    if (fetchFeedbackError) {
      toast({
        title: config.i18n.interviewFeedback.loadFailed,
        description: fetchFeedbackError,
        variant: "destructive",
      });
    }
  }, [fetchFeedbackError, toast, config.i18n.interviewFeedback.loadFailed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      logger.log("[InterviewFeedback] Cleanup - resetting assessment");
      dispatch(resetAssessment());
    };
  }, [dispatch]);

  // Handle share functionality
  const handleShare = useCallback(async () => {
    try {
      const shareData = {
        title: config.i18n.interviewFeedback.shareTitle,
        text: "Check out my conversation performance feedback!",
        url: window.location.href,
      };

      const shared = await shareFeedback(shareData);

      if (shared) {
        toast({
          title: config.i18n.interviewFeedback.shareSuccess,
          description: isShareSupported()
            ? "Feedback shared successfully!"
            : config.i18n.interviewFeedback.linkCopied,
        });
      }
    } catch (error) {
      logger.error("[handleShare] Error:", error);
      toast({
        title: "Share Failed",
        description: "Failed to share feedback. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast, config.i18n.interviewFeedback]);

  // Calculate overall score from gauge block
  const overallScore = useMemo(() => {
    const gaugeBlock = feedbackBlocks.find((b) => b.type === "gauge") as
      | GaugeBlock
      | undefined;
    if (!gaugeBlock) return null;

    // Determine color based on score percentage
    const percentage = (gaugeBlock.value / gaugeBlock.maxValue) * 100;
    let color = "#10b981"; // green
    if (percentage < 50)
      color = "#ef4444"; // red
    else if (percentage < 70)
      color = "#f59e0b"; // amber
    else if (percentage < 85) color = "#3b82f6"; // blue

    return {
      value: gaugeBlock.value,
      maxValue: gaugeBlock.maxValue,
      label: gaugeBlock.title,
      color: color,
    };
  }, [feedbackBlocks]);

  // Handle PDF download
  const handleDownloadPDF = useCallback(async () => {
    try {
      toast({
        title: config.i18n.interviewFeedback.generatingPdf,
        description: "Please wait while we generate your PDF...",
      });

      const fileName = `conversation-feedback-${assessmentId}.pdf`;
      const completedDate =
        feedback?.completed_at ||
        feedback?.created_at ||
        new Date().toISOString();

      await generateFeedbackPDF(
        feedbackBlocks,
        overallScore,
        completedDate,
        fileName
      );

      toast({
        title: config.i18n.interviewFeedback.pdfDownloaded,
        description: config.i18n.interviewFeedback.pdfSuccess,
      });
    } catch (error) {
      logger.error("[handleDownloadPDF] Error:", error);
      toast({
        title: "PDF Generation Failed",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  }, [
    assessmentId,
    feedbackBlocks,
    overallScore,
    feedback,
    toast,
    config.i18n.interviewFeedback,
  ]);

  const handleRetry = () => {
    if (assessmentId) {
      dispatch(fetchAssessmentFeedback(assessmentId));
    }
  };

  const handleNewInterview = () => {
    if (meetingId) {
      MeetingSessionManager.clear(meetingId);
    }
    dispatch(resetAssessment());
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
      {/* Navigation Bar - Fixed */}
      <div className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-12 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (meetingId) {
                MeetingSessionManager.clear(meetingId);
              }
              navigate(backPath);
            }}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {config.i18n.interviewFeedback.goToDashboard}
          </Button>
        </div>
      </div>

      {/* Header - Static */}
      <header className="border-b border-border/40 bg-background/80">
        <div className="container mx-auto px-4 py-6 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold">
              {config.i18n.interviewFeedback.pageTitle}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div id="feedback-content" className="max-w-4xl mx-auto space-y-6">
          {/* Loading State */}
          {isFetchingFeedback && (
            <div className="space-y-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          )}

          {/* Error State */}
          {!isFetchingFeedback && fetchFeedbackError && (
            <Card className="p-8 border-border/50 backdrop-blur-sm bg-card/80">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                  <RotateCcw className="w-8 h-8 text-destructive" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-foreground">
                    {config.i18n.interviewFeedback.loadFailed}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {fetchFeedbackError}
                  </p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button onClick={handleRetry} variant="default">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button onClick={handleNewInterview} variant="outline">
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Success State - Render Feedback Tabs */}
          {!isFetchingFeedback && !fetchFeedbackError && feedback && (
            <>
              {/* Header Info */}
              <Card className="p-6 border-border/50 backdrop-blur-sm bg-card/80">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-2xl font-bold text-foreground">
                      {meetingName}
                    </h2>
                    {meetingCategory && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                        {meetingCategory}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Completed on{" "}
                    {feedback.completed_at || feedback.created_at
                      ? new Date(
                          feedback.completed_at || feedback.created_at
                        ).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Recently"}
                  </p>
                </div>
              </Card>

              {/* Tabbed Feedback Layout */}
              {feedbackBlocks.length === 0 ? (
                <Card className="p-8 border-border/50 backdrop-blur-sm bg-card/80">
                  <div className="text-center space-y-2">
                    <p className="text-muted-foreground">
                      No feedback blocks available.
                    </p>
                  </div>
                </Card>
              ) : (
                <FeedbackTabsLayout
                  blocks={feedbackBlocks}
                  onShare={handleShare}
                  onDownloadPDF={handleDownloadPDF}
                  meetingId={actualSessionId}
                  session={meetingSession}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer - Continue Your Learning Journey */}
      <footer className="bg-white border-t border-border/40 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="mb-12 border-2 border-primary/20 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 overflow-visible relative animate-fade-in">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl" />
              <CardContent className="p-8 sm:p-12 text-center relative z-10 flex flex-col items-center">
                <div className="inline-block mb-4">
                  <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold">
                    🚀 READY TO PRACTICE?
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-4">
                  Continue Your Learning Journey
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  {totalModulesCount > 0
                    ? `Practice with 50+ realistic scenarios and keep improving your English communication skills`
                    : "Practice with realistic scenarios and keep improving your English communication skills"}
                </p>
                <Button
                  size="lg"
                  onClick={() => {
                    if (meetingId) {
                      MeetingSessionManager.clear(meetingId);
                    }
                    navigate("/meeting-simulator");
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 h-auto font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 flex-shrink-0"
                >
                  Start New Practice Session
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </footer>
    </div>
  );
}
